import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  VStack,
  Button,
  ButtonSpinner,
  ButtonText,
  HStack,
  Icon,
  Text,
  Badge,
  BadgeText,
  Pressable,
  Spinner,
  ScrollView,
} from '@gluestack-ui/themed';
import { Info } from 'lucide-react-native';
import SlotHeader from './SlotHeader';
import SlotTasksSection from './SlotTasksSection';
import SlotRegistrationsSection from './SlotRegistrationsSection';
import SlotMeetingActions from './SlotMeetingActions';
import SlotRescheduleSection from './SlotRescheduleSection';
import { useEventCalendarMutations } from '../hooks/useEventCalendarMutations';

/**
 * SlotEditView Component - Displays slot information and allows deletion
 */
const SlotEditView = ({ slot, onSuccess }) => {
  const { t } = useTranslation();
  const {
    confirmDeleteSlot, deleteMutation, confirmSlotManually, confirmSlotMutation,
  } = useEventCalendarMutations(onSuccess);
  // State for progressive disclosure of manual confirmation
  const [showManualConfirm, setShowManualConfirm] = React.useState(false);

  // We could fetch bookings here, but SlotRegistrationsSection already does it
  // For delete confirmation, we'll need to pass the count from that component
  // For now, let's assume we have access to the bookings or calculate it differently
  const [activeBookingsCount, setActiveBookingsCount] = React.useState(0);

  const handleDelete = () => {
    confirmDeleteSlot(slot, activeBookingsCount);
  };

  return (
    <VStack space="sm" flex={1} testID={`lecturer-calendar-slot-sidebar-${slot.slotId}`}>
      <ScrollView flex={1}>
        <VStack space="sm" pb="$4">
          {/* Header Section - Date and Time */}
          <SlotHeader slot={slot} />

          {/* Teams Meeting Note for Planned Slots */}
          {slot.status === 'planned' && (
            <VStack space="sm">
              <Badge action="info" variant="solid" size="md" w="100%" p="$3">
                <HStack space="sm" alignItems="flex-start" flex={1}>
                  <Icon as={Info} size="sm" mt="$0.5" color="$lightBlue600" />
                  <VStack flex={1} space="xs">
                    <BadgeText size="xs" flex={1}>
                      {t('lecturer.eventCalendar.sidebar.plannedSlotNote')}
                    </BadgeText>
                    {!showManualConfirm && (
                      <Pressable onPress={() => setShowManualConfirm(true)}>
                        <Text
                          size="xs"
                          color="$lightBlue700"
                          textDecorationLine="underline"
                          fontWeight="semibold"
                        >
                          {t('lecturer.eventCalendar.sidebar.havingTroubleConfirming')}
                        </Text>
                      </Pressable>
                    )}
                  </VStack>
                </HStack>
              </Badge>

              {showManualConfirm && (
                <VStack
                  space="sm"
                  p="$3"
                  bg="$backgroundLight50"
                  borderRadius="$md"
                  borderWidth={1}
                  borderColor="$borderLight200"
                >
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.eventCalendar.sidebar.manualConfirmExplain')}
                  </Text>
                  <Button
                    size="xs"
                    action="primary"
                    onPress={() => confirmSlotManually(slot)}
                    isDisabled={confirmSlotMutation.isPending}
                  >
                    {confirmSlotMutation.isPending ? (
                      <HStack space="sm" alignItems="center">
                        <Spinner size="small" color="$primary600" />
                        <ButtonText>{t('lecturer.eventCalendar.sidebar.confirming')}</ButtonText>
                      </HStack>
                    ) : (
                      <ButtonText>{t('lecturer.eventCalendar.sidebar.confirmSlotManual')}</ButtonText>
                    )}
                  </Button>
                </VStack>
              )}
            </VStack>
          )}

          {/* Session Tasks Section */}
          <SlotTasksSection boundTasks={slot.boundTasks || []} boundTaskIds={slot.boundTaskIds || []} />

          {/* Registrations Section - now part of the scroll view */}
          <SlotRegistrationsSection
            slot={slot}
            onActiveBookingsChange={setActiveBookingsCount}
            scrollable={false}
          />
        </VStack>
      </ScrollView>

      {/* Bottom actions (always visible) */}
      <VStack space="sm" mt="auto">
        <SlotMeetingActions slot={slot} />

        <SlotRescheduleSection
          slot={slot}
          activeBookingsCount={activeBookingsCount}
          onSuccess={onSuccess}
        />

        {/* Cancel button */}
        <Button
          size="sm"
          onPress={handleDelete}
          disabled={deleteMutation.isPending}
          variant="outline"
          borderColor="$red400"
          _pressed={{ bg: '$red50' }}
        >
          {deleteMutation.isPending ? (
            <HStack space="sm" alignItems="center">
              <ButtonSpinner color="$red500" />
              <ButtonText color="$red500" size="sm">
                {t('lecturer.eventCalendar.sidebar.deletingAndCancellingInvites')}
              </ButtonText>
            </HStack>
          ) : (
            <ButtonText color="$red500" size="sm">
              {t('lecturer.eventCalendar.sidebar.delete')}
            </ButtonText>
          )}
        </Button>
      </VStack>
    </VStack>
  );
};

export default SlotEditView;
